// apps/web/src/api/cargo-write.ts — typed wrappers around the Cargo write,
// import, tariff, and payment workflow endpoints (#1232). Reads stay in
// `cargo.ts`; this module carries the mutating surface so neither file
// approaches the size limit. All callers route through the shared `request<T>`
// so the coordinated 401-retry / `ApiClientError` surface and the
// response-schema parse are preserved.

import {
  CargoArticleWireSchema,
  CargoImportResponseSchema,
  type CargoArticleWire,
  type CargoClientPaymentReviewBody,
  type CargoClientPaymentSubmitBody,
  type CargoImportRequest,
  type CargoImportResponse,
  type CreateCargoArticleInput,
  type UpdateCargoArticleInput,
} from '@magimp/shared';
import { z } from 'zod';

import { request } from './client.js';

const NoContentSchema = z.void();

export function createCargo(input: CreateCargoArticleInput): Promise<CargoArticleWire> {
  return request<CargoArticleWire>(
    '/cargo',
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CargoArticleWireSchema,
  );
}

export function updateCargo(id: string, patch: UpdateCargoArticleInput): Promise<CargoArticleWire> {
  return request<CargoArticleWire>(
    `/cargo/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CargoArticleWireSchema,
  );
}

export function deleteCargo(id: string): Promise<void> {
  return request<void>(
    `/cargo/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    {},
    NoContentSchema,
  );
}

export function importCargo(body: CargoImportRequest): Promise<CargoImportResponse> {
  return request<CargoImportResponse>(
    '/cargo/import',
    { method: 'POST', body: JSON.stringify(body), headers: { 'content-type': 'application/json' } },
    {},
    CargoImportResponseSchema,
  );
}

export function setCargoClientTariff(
  id: string,
  transportationTariffForClients: string | null,
): Promise<void> {
  return request<void>(
    `/cargo/${encodeURIComponent(id)}/client-tariff`,
    {
      method: 'PATCH',
      body: JSON.stringify({ transportationTariffForClients }),
      headers: { 'content-type': 'application/json' },
    },
    {},
    NoContentSchema,
  );
}

// #1936 staged wire change: the manager-submit endpoint now takes
// `{ requestedStatus }` (paid | partially_paid), NOT the old `{ status }`. The
// backend strict schema 400s the old body, so the legacy `setCargoClientPaymentStatus`
// is retired in favour of this submit + the financier review below.
export function submitCargoClientPayment(
  id: string,
  requestedStatus: CargoClientPaymentSubmitBody['requestedStatus'],
): Promise<void> {
  return request<void>(
    `/cargo/${encodeURIComponent(id)}/client-payment-status`,
    {
      method: 'PATCH',
      body: JSON.stringify({ requestedStatus }),
      headers: { 'content-type': 'application/json' },
    },
    {},
    NoContentSchema,
  );
}

export function reviewCargoClientPayment(
  id: string,
  body: CargoClientPaymentReviewBody,
): Promise<void> {
  return request<void>(
    `/cargo/${encodeURIComponent(id)}/client-payment-review`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    NoContentSchema,
  );
}
